import { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1=mobile, 2=otp, 3=new password
  const [mobile, setMobile] = useState('');
  const [otp, setOtp] = useState('');
  const [otpToken, setOtpToken] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const apiBase = '/api/admin/auth'; // works for all roles now

  const sendOtp = async () => {
    if (!/^\d{10}$/.test(mobile)) { setError('Enter a valid 10-digit mobile number.'); return; }
    setError(''); setLoading(true);
    try {
      await axios.post(`${API_URL}${apiBase}/send-otp`, { mobile_number: mobile, purpose: 'password_reset' });
      setStep(2);
    } catch (err) { setError(err.response?.data?.error || 'Failed to send OTP.'); }
    finally { setLoading(false); }
  };

  const verifyOtp = async () => {
    setError(''); setLoading(true);
    try {
      const res = await axios.post(`${API_URL}${apiBase}/verify-otp`, { mobile_number: mobile, otp, purpose: 'password_reset' });
      setOtpToken(res.data.token);
      setStep(3);
    } catch (err) { setError(err.response?.data?.error || 'Invalid OTP.'); }
    finally { setLoading(false); }
  };

  const resetPassword = async () => {
    if (password !== confirmPassword) { setError('Passwords do not match.'); return; }
    if (password.length < 4) { setError('Password must be at least 4 digits.'); return; }
    setError(''); setLoading(true);
    try {
      await axios.post(`${API_URL}${apiBase}/reset-password`, { otp_token: otpToken, password });
      navigate('/login', { state: { message: 'Password reset successfully. Please sign in.' } });
    } catch (err) { setError(err.response?.data?.error || 'Reset failed.'); }
    finally { setLoading(false); }
  };

  return (
    <div style={s.page}>
      <div style={s.card}>
        <div style={s.header}>
          <div style={s.logo}>🔑</div>
          <h1 style={s.title}>Reset Password</h1>
        </div>

        {step === 1 && (
          <div style={s.form}>
            <p style={{ ...s.hint, marginBottom: 4 }}>Enter your registered mobile number — OTP will be sent to WhatsApp</p>
            <div style={s.field}>
              <label style={s.label}>Mobile Number</label>
              <input style={s.input} type="tel" placeholder="9876543210" value={mobile}
                onChange={e => setMobile(e.target.value)} maxLength={10} />
            </div>
            {error && <div style={s.error}>{error}</div>}
            <button style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }} onClick={sendOtp} disabled={loading}>
              {loading ? 'Sending…' : 'Send OTP to WhatsApp'}
            </button>
          </div>
        )}

        {step === 2 && (
          <div style={s.form}>
            <p style={s.hint}>OTP sent to WhatsApp: <strong>+91 {mobile}</strong></p>
            <div style={s.field}>
              <label style={s.label}>Enter OTP</label>
              <input style={{ ...s.input, letterSpacing: '0.5em', fontSize: '1.5rem', textAlign: 'center' }}
                type="text" inputMode="numeric" placeholder="••••" value={otp}
                onChange={e => setOtp(e.target.value)} maxLength={4} />
            </div>
            {error && <div style={s.error}>{error}</div>}
            <button style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }} onClick={verifyOtp} disabled={loading}>
              {loading ? 'Verifying...' : 'Verify OTP'}
            </button>
          </div>
        )}

        {step === 3 && (
          <div style={s.form}>
            <div style={s.field}>
              <label style={s.label}>New Password</label>
              <input style={s.input} type="password" placeholder="4+ digits" value={password}
                onChange={e => setPassword(e.target.value)} />
            </div>
            <div style={s.field}>
              <label style={s.label}>Confirm Password</label>
              <input style={s.input} type="password" placeholder="Repeat password" value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)} />
            </div>
            {error && <div style={s.error}>{error}</div>}
            <button style={{ ...s.btn, ...(loading ? s.btnDisabled : {}) }} onClick={resetPassword} disabled={loading}>
              {loading ? 'Resetting...' : 'Set New Password'}
            </button>
          </div>
        )}

        <p style={s.loginLink}><Link to="/login" style={s.link}>Back to Sign In</Link></p>
      </div>
    </div>
  );
};

const s = {
  page: { minHeight: '100vh', background: 'linear-gradient(135deg, #1a1a2e 0%, #0f3460 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem', fontFamily: "'Segoe UI', system-ui, sans-serif" },
  card: { background: '#fff', borderRadius: '1.5rem', padding: '2.5rem', width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' },
  header: { textAlign: 'center', marginBottom: '1.5rem' },
  logo: { fontSize: '2.5rem', marginBottom: '0.5rem' },
  title: { fontSize: '1.5rem', fontWeight: '800', color: '#1a1a2e', margin: 0 },
  roleToggle: { display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' },
  roleBtn: { flex: 1, padding: '0.65rem', border: '1.5px solid #e5e7eb', borderRadius: '0.75rem', background: '#f9fafb', color: '#555', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' },
  roleBtnActive: { background: '#1a1a2e', color: '#fff', borderColor: '#1a1a2e' },
  form: { display: 'flex', flexDirection: 'column', gap: '1rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.35rem' },
  label: { fontSize: '0.75rem', fontWeight: '700', color: '#555', textTransform: 'uppercase', letterSpacing: '0.05em' },
  input: { padding: '0.75rem 1rem', border: '1.5px solid #e5e7eb', borderRadius: '0.75rem', fontSize: '1rem', outline: 'none' },
  hint: { color: '#555', fontSize: '0.9rem', textAlign: 'center', margin: 0 },
  error: { background: '#fef2f2', border: '1px solid #fecaca', borderRadius: '0.5rem', padding: '0.75rem', color: '#dc2626', fontSize: '0.875rem' },
  btn: { padding: '0.875rem', background: 'linear-gradient(135deg, #1a1a2e, #0f3460)', color: '#fff', border: 'none', borderRadius: '0.75rem', fontWeight: '700', fontSize: '1rem', cursor: 'pointer' },
  btnDisabled: { opacity: 0.6, cursor: 'not-allowed' },
  loginLink: { textAlign: 'center', marginTop: '1.5rem', fontSize: '0.875rem', color: '#888' },
  link: { color: '#4f46e5', fontWeight: 600, textDecoration: 'none' }
};

export default ForgotPassword;
